import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LESSON_1_1_PROMPTS,
  LESSON_1_1_PROMPT_LABELS,
  readAnswer,
} from '../lib/answerStore';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';
import { OperatorStackCanvas } from '../components/lesson/OperatorStackCanvas';

/**
 * Lesson 8.5 visual preview at /preview/lesson/8.5
 * Week 8 capstone (final lesson of the program): "The operator: your first 90 days after this program"
 * 8-section format like 7.5. Vocab: Operator.
 * Student ships a one-page operator brief to themselves.
 */

export default function LessonPreviewPage8_5() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-brief',
  'sec-map',
  'sec-stage-1',
  'sec-stage-2',
  'sec-stage-3',
  'sec-stage-4',
  'sec-forward',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 8;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[210px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <Brief />
          <Map />
          <Stage1 />
          <Stage2 />
          <Stage3 />
          <Stage4 />
          <CarryForward />
          <LockCard />
          <Graduation />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 8 · AI has impacted everything · Lesson 8.5 · Capstone · Final lesson
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The operator: your first 90 days after this program
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 75 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Operator · Ship · Constare</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        One person. One one-page operator brief. Written for yourself, today.
      </p>
    </header>
  );
}

/* --- Section 1: Brief --- */
function Brief() {
  return (
    <Section id="sec-brief" kicker="The brief" title="Two people. Same program. Same week 9. One has an operator brief. One does not.">
      <p className="mt-6 text-text/90">
        Two people finish this program in the same week. Same skills, same
        ambition, same access to the same tools. Ninety days later, one is
        operating at a higher level than where they started. The other has
        drifted back to old habits, picked up a new tool, dropped it, picked
        up another, and quietly stopped using most of what they learned.
        The gap is not talent. The gap is whether they wrote any of it down.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Person A · No operator brief
          </p>
          <p className="mt-3 text-text/90 italic">
            Finishes the program. Feels sharp for two weeks. Sees a new model
            launch and tries it. Reads a thread about a new agent tool, signs
            up, abandons it. By day 60 they cannot say which anchors they
            protect, which tools earn their stack, or when they last re-proved
            a skill without AI. Everything they learned is in there somewhere.
            Nothing is in arm&apos;s reach.
          </p>
        </div>
        <div className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            Person B · Has an operator brief
          </p>
          <p className="mt-3 text-text/90 italic">
            Finishes the program with one page. Three anchors named. A weekly
            rhythm written out. A starter stack of 1 to 3 tools, with the job
            each one does. A re-prove ritual on the calendar. A team standard
            and a learning loop. When a new model drops, they check it against
            the brief instead of their feed. By day 90 they have recalibrated
            once. The brief still fits.
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Assignment
        </p>
        <h3 className="mt-2 text-xl font-semibold text-text leading-snug">
          Write a one-page operator brief for yourself. Eight sections. The
          first 90 days after this program, in writing, today.
        </h3>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <BriefRow label="What" value="A one-page document, eight short sections, that names how you will operate after this program: anchors, rhythm, stack, ritual, standard, learning loop." />
          <BriefRow label="Audience" value="You. This is private by default. No LinkedIn version. No public commitment. The point is honesty, not performance." />
          <BriefRow label="Length" value="One page. If it spills to two, you are over-explaining. Cut until it fits on a single page you can reread in two minutes." />
          <BriefRow label="Ship" value="Save it somewhere you will actually open again: Notes app pinned, a doc on your home screen, printed and taped to your desk. The brief is only real if you can find it." />
        </div>
      </div>

      <Subhead n={1} title="The brief is for you, not for show" className="mt-10" />
      <p className="mt-4 text-text/90">
        Every other capstone in this program shipped to a real audience: a
        community for 5.5, a real reader for the Week 6 build, a team for 7.5. The
        operator brief is the one that does not ship outward. It ships
        inward, to the person who has to keep operating in a world where AI
        has impacted everything. That person is you, and you are the only
        audience that matters here.
      </p>
      <BriefArtifacts />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Before you read on
        </p>
        <p className="mt-3 text-text/90">
          Open a blank document. Title it: My operator brief, [today&apos;s
          date]. Leave the eight sections blank. The rest of the lesson is
          the work of filling them in, one stage at a time, with the work
          you have already done across eight weeks.
        </p>
      </div>
    </Section>
  );
}

function BriefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/90">{value}</p>
    </div>
  );
}

const BRIEF_ARTIFACTS = [
  {
    n: '01',
    name: 'A document, not a mindset',
    accent: 'emerald' as const,
    why: 'Operators write things down. Mindsets evaporate when the next model launches. The brief exists so you do not have to re-decide what you already know.',
  },
  {
    n: '02',
    name: 'One page, not a manifesto',
    accent: 'amber' as const,
    why: 'A two-page brief is one you reread once and never again. A one-pager is one you reread in two minutes on a Sunday. Cut until it fits.',
  },
  {
    n: '03',
    name: 'Private by default',
    accent: 'emerald' as const,
    why: 'No LinkedIn post. No commitment thread. The point of the operator brief is to be honest with yourself about how you will work, not to perform a clean version of it.',
  },
  {
    n: '04',
    name: 'Built to recalibrate',
    accent: 'amber' as const,
    why: 'You will revise this brief. Probably at day 30 or 60. The document is meant to age and update, not to lock you in. Date it. Plan to rewrite it.',
  },
];

function BriefArtifacts() {
  return (
    <div className="mt-6 space-y-3">
      {BRIEF_ARTIFACTS.map((a) => {
        const isEm = a.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={a.n} className={`rounded-lg border ${borderCls} ${bgCls} p-4`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">{a.n}</span>
              <h4 className={`text-base font-semibold ${labelCls}`}>{a.name}</h4>
            </div>
            <p className="mt-2 text-sm text-text/85">{a.why}</p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 2: Map --- */
function Map() {
  return (
    <Section id="sec-map" kicker="The map" title="Week 8 was the four moves for living in a world where AI has impacted everything. The operator brief is all four, in writing.">
      <p className="mt-6 text-text/90">
        Capability unlock. Anchor. Drift. Fit. Four lessons, four moves you can run on
        yourself. The capstone is the document that bakes those moves into
        how you work, so you stop having to rebuild them every time a new
        model lands in your feed.
      </p>
      <MapDiagram />
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The Week 8 vocab spine
        </p>
        <p className="mt-3 text-text/90 leading-relaxed">
          <span className="font-mono text-[10px] text-amber mr-2">8.1</span>
          Capability unlock ·
          <span className="font-mono text-[10px] text-amber mx-2">8.2</span>
          Anchor ·
          <span className="font-mono text-[10px] text-amber mx-2">8.3</span>
          Drift ·
          <span className="font-mono text-[10px] text-amber mx-2">8.4</span>
          Fit ·
          <span className="font-mono text-[10px] text-amber mx-2">8.5</span>
          Operator
        </p>
      </div>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The vocab for the capstone
        </p>
        <p className="mt-3 text-text/90">
          <span className="font-semibold text-text">Operator.</span> A person
          who works inside a world that has been changed by AI without losing
          track of what is theirs to do. An operator has anchors, a rhythm, a
          stack, a re-prove ritual, and a way to read real unlocks vs. noise.
          The operator brief is the one-page document that holds those things
          together so you can keep working when the next tool, model, or
          reorganization arrives.
        </p>
      </div>
    </Section>
  );
}

const MOVES = [
  {
    n: '01',
    name: '8.1 · Capability unlock',
    accent: 'emerald' as const,
    output: 'Tell real capability changes apart from headlines. Stop reacting to noise.',
  },
  {
    n: '02',
    name: '8.2 · Anchor',
    accent: 'amber' as const,
    output: 'Name the parts of your work whose value depends on you being responsible.',
  },
  {
    n: '03',
    name: '8.3 · Drift',
    accent: 'emerald' as const,
    output: 'Catch the quiet erosion of judgment. Re-prove one task a week without AI.',
  },
  {
    n: '04',
    name: '8.4 · Fit',
    accent: 'amber' as const,
    output: 'Match tools to the job. Stack of 1 to 3, with the work each one is for.',
  },
  {
    n: '05',
    name: '8.5 · Operator',
    accent: 'emerald' as const,
    output: 'Write the four moves above into a one-page brief you reread on Sundays.',
  },
];

function MapDiagram() {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {MOVES.map((w) => {
        const isEm = w.accent === 'emerald';
        const tagCls = isEm ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber/15 text-amber';
        return (
          <div key={w.n} className="rounded-lg border border-line bg-surface p-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-[10px] text-muted">{w.n}</span>
              <span
                className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tagCls}`}
              >
                {w.name}
              </span>
            </div>
            <p className="mt-3 text-sm text-text/85">{w.output}</p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 3: Stage 1 Inventory --- */
function Stage1() {
  return (
    <Section id="sec-stage-1" kicker="Stage 1 · Inventory" title="Before you draft the brief, take honest stock of where you are right now.">
      <WeekOneCallback />
      <p className="mt-6 text-text/90">
        An operator brief built on a fantasy version of your own habits is
        a brief you will quietly ignore. Stage 1 is the inventory pass: what
        you actually do today with AI, what you avoid, where you have drifted,
        what is working. You are not writing yet. You are getting the picture
        honest so the brief is built on the real you, not the aspirational one.
      </p>

      <StageBlock
        label="The move you make on paper"
        accent="emerald"
        body="Write four short lists for yourself. (1) Where AI is already part of how I work, by category from Lesson 8.4 (chat &amp; research, coding, voice, image &amp; video, orchestration). (2) Where I have started to drift (signals from Lesson 8.3: empty-handed amnesia, tool-or-nothing, skipped reading, defense gap, conversation outsourcing). (3) What I have stopped doing in the last six months. (4) What I am proud of in my current work, AI or not. Keep each list to 3 to 5 items. Be specific, not heroic."
      />

      <StageBlock
        label="The prompt you give AI"
        accent="amber"
        body="&ldquo;Here are four lists about how I currently work. Without telling me what to do, what patterns do you notice across them? Where is there tension between what I do today and what I say I value? Name one thing I am probably underestimating and one thing I am probably overestimating. Do not write my operator brief.&rdquo; AI is a second pair of eyes on a picture only you can see. The inventory stays yours."
      />

      <StageBlock
        label="The reality check"
        accent="emerald"
        body="Pick one item from the drift list and one from the proud list. Ask: have I actually seen this in my work in the last two weeks, or am I writing from a self-image? If you cannot point to a concrete moment in the last two weeks, the item is too clean. Rewrite it with a moment attached, or cut it. The inventory only helps if it is built from observable behavior, not adjectives."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <ul className="mt-3 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            Four short lists (where AI shows up, where you are drifting, what you have dropped, what you are proud of).
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One sentence on the gap between what you do today and what you want to be true.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One specific moment from the last two weeks tied to each list item.
          </li>
        </ul>
      </div>
    </Section>
  );
}

/* --- Week 1 calibration callback --- */
function WeekOneCallback() {
  const slugs = [
    LESSON_1_1_PROMPTS.STUDENT_AB,
    LESSON_1_1_PROMPTS.SKIPPED,
    LESSON_1_1_PROMPTS.DID_THINKING,
  ];
  const entries = slugs
    .map((slug) => ({ slug, answer: readAnswer('1.1', slug) }))
    .filter((e) => e.answer.trim().length > 0);

  if (entries.length === 0) {
    return (
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Your Week 1 calibration
        </p>
        <p className="mt-3 text-text/90">
          Lesson 1.1 asked you three calibration questions and saves your
          answers to this browser. We do not see any answers from this device.
          That can mean two things: you did the lesson on a different device,
          or you skipped the writing. If you did the writing somewhere else,
          pull up those notes before you start the inventory. If you skipped
          it, the inventory below still works, but you will not get the
          before-and-after read.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
        Your Week 1 calibration
      </p>
      <p className="mt-3 text-text/90">
        Eight weeks ago, in Lesson 1.1, you answered three questions about
        where you stood with AI. Here is what you wrote. Read them once
        before you start the inventory below. The point is not to judge
        the old answer. The point is to notice what has actually changed.
      </p>
      <ol className="mt-5 space-y-4">
        {entries.map(({ slug, answer }, i) => (
          <li key={slug} className="rounded-md border border-line bg-ink/40 p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
              Week 1 · Q{i + 1}
            </p>
            <p className="mt-1 text-sm text-text/90">
              {LESSON_1_1_PROMPT_LABELS[slug]}
            </p>
            <p className="mt-3 whitespace-pre-wrap text-text">{answer}</p>
          </li>
        ))}
      </ol>
      <p className="mt-5 text-sm text-muted">
        Hold these in mind as you take inventory. The gap (or lack of gap)
        between then and now is part of what the operator brief is built on.
      </p>
    </div>
  );
}

/* --- Section 4: Stage 2 Draft the eight sections --- */
function Stage2() {
  return (
    <Section id="sec-stage-2" kicker="Stage 2 · Draft the eight sections" title="One page. Eight short sections. Each one decides a real question your next 90 days will ask of you.">
      <p className="mt-6 text-text/90">
        Every operator brief worth writing has the same eight sections. They
        are short on purpose. The point is not to map your whole life; the
        point is to decide the eight questions your next 90 days will ask
        of you, before they ask, so you have answers in writing instead of
        in your head.
      </p>

      <StageBlock
        label="Section 01 · Where you are now"
        accent="emerald"
        body="One paragraph (3 to 5 sentences). Where you sit today: role or class, what kind of work you are doing, where AI fits, where you feel sharp, where you feel rusty. Honest, not impressive. This is the baseline that the next 90 days will move from. If your description matches your LinkedIn headline, rewrite it."
      />

      <StageBlock
        label="Section 02 · Your three anchors"
        accent="amber"
        body="From Lesson 8.2. Name the three parts of your work whose value depends on you being responsible. One from Judgment, one from Standing, one from Relationship is the cleanest split. Be specific: not &ldquo;writing&rdquo; but &ldquo;the cover letter when I apply for the role I actually want.&rdquo; Not &ldquo;leadership&rdquo; but &ldquo;the first 1:1 with anyone new on my team.&rdquo; The anchors are the lines AI does not cross."
      />

      <StageBlock
        label="Section 03 · Your weekly AI rhythm"
        accent="emerald"
        body="Two short lists. When AI is the default tool in your week (specific tasks: summarizing readings, drafting recap emails, generating practice questions, cleaning up writing). When AI is off (specific moments: the first draft of an anchor, your re-prove task, the first read of a new topic, conversations with people who deserve your full attention). Three items per list is plenty. Rhythm beats willpower."
      />

      <StageBlock
        label="Section 04 · Your starter stack"
        accent="amber"
        body="From Lesson 8.4. 1 to 3 tools by category, with a one-sentence job for each. Example: &ldquo;Chat for first drafts and research. Voice for transcribing my own interviews. Orchestration for moving form submissions into the right folder.&rdquo; No brands required; categories are enough. If you cannot say what job a tool is doing in one sentence, it does not earn the stack."
      />

      <OperatorStackCanvas />

      <StageBlock
        label="Section 05 · Your re-prove ritual"
        accent="emerald"
        body="From Lesson 8.3. One task a week, no AI, then compare and write one sentence about what only you could see. Name the day, the task, and where the one sentence goes. Example: &ldquo;Sunday evening. Re-read my own writing from the week and rewrite one paragraph by hand. Notes app, tagged #reprove.&rdquo; A ritual with no day and no place is not a ritual."
      />

      <StageBlock
        label="Section 06 · Your team standard"
        accent="amber"
        body="Carried forward from Lesson 7.5, made personal. Two or three lines about how you show up on the team you are on right now: how you disclose AI use, where you hold a line on craft, how you handle disagreements without making them about identity. If you do not have a team yet, write what you will bring to the first one you join. Standards travel with the person who carries them."
      />

      <StageBlock
        label="Section 07 · Your learning loop"
        accent="emerald"
        body="From Lesson 8.1. How you catch real unlocks vs. noise. One source you actually read (a newsletter, a person, a doc). One review cadence (weekly, thirty minutes). One question you ask each week: &ldquo;What changed for me in the last seven days, and what stayed the same?&rdquo; The loop is not about being early. It is about not being whiplashed by every launch."
      />

      <StageBlock
        label="Section 08 · Your recalibration date"
        accent="amber"
        body="Pick a date 30 to 60 days from today. Write it on the brief. On that date, you reread this page, mark what changed, and rewrite the parts that no longer fit. The brief is not a contract. It is a snapshot of the operator you are choosing to be right now, and the date is the promise to look at it again before it ages into nonsense."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A one-page draft with all eight sections written out. Each section
          is short (2 to 5 sentences max). Specific to your real work, not
          abstractions. If you cannot name an actual task, anchor, or moment
          inside a section, that section is too generic; rewrite it with
          your real life in mind.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 5: Stage 3 Pressure-test --- */
function Stage3() {
  return (
    <Section id="sec-stage-3" kicker="Stage 3 · Pressure-test against your own week" title="A brief is real only if it would have changed something you already did. Read it back against your last week.">
      <p className="mt-6 text-text/90">
        A brief written for an idealized week is a brief that fails the first
        ordinary one. The pressure test is to read the draft against the
        week you actually just lived through, and see whether the brief
        would have changed anything. If it would not have, the brief is
        decoration.
      </p>

      <StageBlock
        label="Replay the last seven days"
        accent="amber"
        body="Open your calendar, your sent messages, your notes from the last week. For each weekday, write one line: what was the most important piece of work I did that day, and how was AI involved? No theory, no should-haves. Just what happened. Five lines for five days. If you skip days, write &ldquo;nothing&rdquo; rather than inventing."
      />

      <StageBlock
        label="Run the brief against the replay"
        accent="emerald"
        body="For each of the five lines, ask: would my draft operator brief have changed how I handled this? Where? Which section? If your brief would not have changed anything in the entire week, it is too vague. If it would have changed one or two moments cleanly, that is the right level. If it would have changed every moment, you are over-promising and will quit it by day 14."
      />

      <StageBlock
        label="The prompt that helps stress-test"
        accent="amber"
        body="&ldquo;Here is my draft operator brief. Here are five real moments from my last week. Without rewriting the brief, tell me where a section is too vague to have changed anything in those moments, and where I am setting up a rule I am unlikely to keep. Do not be polite about it.&rdquo; AI rehearses the friction. You do the editing."
      />

      <StageBlock
        label="Then revise"
        accent="emerald"
        body="Edit the brief. Cut sections that would not have changed last week. Tighten sections that almost worked. If you keep a section that did not show up in the replay, write one sentence about why you believe in it anyway. A brief with one self-aware exception is stronger than a brief that pretends every section was already lived."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A revised one-page brief. At least three of the eight sections
          have been edited based on the last-week replay. You can point to a
          specific moment from your real week where each section would have
          changed something. The brief is now built from observed behavior,
          not from intentions.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 6: Stage 4 Ship to yourself --- */
function Stage4() {
  return (
    <Section id="sec-stage-4" kicker="Stage 4 · Ship it to yourself" title="One place. One reread. Saved today.">
      <p className="mt-6 text-text/90">
        Every other capstone shipped outward: to a community, to a public,
        to a team. This one ships inward. The ship is not a send button. It
        is choosing the place the brief will live, putting it there today,
        and locking in the first reread. A brief saved in a folder you will
        not open is not shipped.
      </p>

      <StageBlock
        label="Pass 1 · The honesty pass"
        accent="emerald"
        body="Read the whole brief one more time. Is there a single sentence in it that you wrote because it sounded good rather than because it was true? Any anchor you do not actually believe is yours? Any rhythm you know you will not keep past day 10? Cut it now. The brief is private, which means there is no excuse for performance. Honesty is the whole point."
      />

      <StageBlock
        label="Pass 2 · The place pass"
        accent="amber"
        body="Pick the one place this brief will live. Options: Notes app pinned to the top, a doc on the home screen of your phone, a printout taped where you work, a daily-open file in your editor. Not buried in a folder. Not in your downloads. One place you actually open. If you cannot think of one, that is a signal about whether you really plan to reread it."
      />

      <StageBlock
        label="Ship"
        accent="emerald"
        body="Save it. Pin it. Print it. Whatever the place pass decided, do it now, in this lesson. Then add one event to your calendar at your recalibration date: &ldquo;Reread my operator brief. Edit what no longer fits.&rdquo; Fifteen minutes, on the day you chose. The brief is shipped when the next reread has a date."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A one-page operator brief, saved in a place you will open, with a
          calendar event for the first reread. One sentence in your notes
          about which section felt hardest to write: where you are now, the
          anchors, the rhythm, the stack, the ritual, the standard, the
          learning loop, or the recalibration date.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 7: Carry forward --- */
function CarryForward() {
  return (
    <Section id="sec-forward" kicker="What you carry forward" title="Five habits to keep. Five traps to remember.">
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Habits to keep
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">01</span>
              Reread the brief on a regular cadence. Sundays are quiet. Use them.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">02</span>
              Re-prove one task a week without AI. The ritual is what keeps the anchors honest.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">03</span>
              Test new tools against the Fit questions before they earn the stack.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">04</span>
              Track real Unlocks, not headlines. Weekly review beats daily scroll.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">05</span>
              Rewrite the brief on your recalibration date. Snapshots, not contracts.
            </li>
          </ul>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Traps to remember
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">01</span>
              Writing the brief you wish you lived instead of the one you actually will.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">02</span>
              Saving it in a folder you never open. A buried brief is no brief.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">03</span>
              Adding tools to the stack because they are new, not because they fit.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">04</span>
              Skipping the re-prove ritual because the week is busy. The busy weeks are exactly when it matters.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">05</span>
              Treating the brief as final. It is meant to age and be rewritten.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The bigger move
        </p>
        <p className="mt-3 text-text/90">
          Eight weeks ago, you started by deciding what you would and would
          not trust AI to do for you. Today, you have done the same thing
          for your next 90 days, in writing, on one page. The brief is yours.
          The anchors are yours. The ritual is yours. AI helped you draft.
          AI did not name your anchors, did not pick the place the brief
          lives, did not write what only you could see. You did.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 8: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card · Week 8" title="Screenshot this. AI has impacted everything, on one card.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Week 8 · Lock card · AI has impacted everything
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          The whole point of Week 8: keep operating in a world AI has
          changed without losing what is yours to do. Track the Unlocks.
          Protect your Anchors. Catch the Drift. Pick for Fit. Live as the
          Operator.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <WeekTile n="01" name="Capability unlock" lesson="8.1" body="Real capability change vs. headline noise. Weekly review, not daily scroll. Four tests: capability, access, workload, stability." />
          <WeekTile n="02" name="Anchor" lesson="8.2" body="The parts of your work whose value depends on you being responsible. Three anchors: Judgment, Standing, Relationship." />
          <WeekTile n="03" name="Drift" lesson="8.3" body="The quiet erosion of judgment from over-reliance. One task a week, no AI, one sentence about what only you could see." />
          <WeekTile n="04" name="Fit" lesson="8.4" body="The job before the tool. Five categories, three Fit questions, a starter stack of 1 to 3 with a one-sentence job each." />
          <WeekTile n="05" name="Operator" lesson="8.5" body="One page, eight sections, private by default. Reread on Sundays. Rewrite on your recalibration date." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The operator brief, in eight sections
          </p>
          <p className="mt-3 text-text/90 leading-relaxed">
            <span className="font-mono text-[10px] text-amber mr-2">01</span>
            Where you are now ·
            <span className="font-mono text-[10px] text-amber mx-2">02</span>
            Your three anchors ·
            <span className="font-mono text-[10px] text-amber mx-2">03</span>
            Your weekly rhythm ·
            <span className="font-mono text-[10px] text-amber mx-2">04</span>
            Your starter stack ·
            <span className="font-mono text-[10px] text-amber mx-2">05</span>
            Your re-prove ritual ·
            <span className="font-mono text-[10px] text-amber mx-2">06</span>
            Your team standard ·
            <span className="font-mono text-[10px] text-amber mx-2">07</span>
            Your learning loop ·
            <span className="font-mono text-[10px] text-amber mx-2">08</span>
            Your recalibration date
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          End of Week 8. End of the program. You have a way to work that
          survives the next model, the next tool, the next reorganization.
          The brief is private. The cadence is yours. Come back when you
          need to recalibrate.
        </p>
      </div>
    </Section>
  );
}

function WeekTile({
  n,
  name,
  lesson,
  body,
}: {
  n: string;
  name: string;
  lesson: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="font-mono text-[10px] text-muted">{lesson}</span>
      </div>
      <div className="mt-2">
        <span className="inline-block rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Graduation (replaces NextUp for final lesson) --- */
function Graduation() {
  return (
    <div className="mt-14 rounded-2xl border border-emerald-500/40 bg-gradient-to-b from-emerald/[0.06] to-transparent p-7">
      <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
        Constare · To stand together
      </p>
      <h3 className="mt-3 text-2xl font-semibold leading-snug text-text">
        Not &ldquo;you&apos;re done.&rdquo;
      </h3>
      <p className="mt-4 text-text/90 leading-relaxed">
        Eight weeks ago, the first lesson was about Constare: to stand
        together. The premise of the program is that AI is a teammate that
        does work with you and for you, not a replacement for human
        connection and communication. The whole arc is for you to come out
        the other side as the operator who can stand with the work, not
        behind it.
      </p>
      <p className="mt-4 text-text/90 leading-relaxed">
        You have an operator brief now. You have a vocabulary: Constare,
        Verify, Source, Token, Hallucination, Context, Prompt, Decompose,
        Audit, Shippable, Judgment, Through-line, Particular, Defensible,
        Stakes, Standing, Audience, Receipt, Voice, Spine, Claim, Ask,
        Payoff, Portfolio, Tell, Human override, Carve-out, Frame, Standard, Capability unlock,
        Anchor, Drift, Fit, Operator. Thirty-four words for a kind of work
        that did not have a vocabulary five years ago.
      </p>
      <p className="mt-4 text-text/90 leading-relaxed">
        The program does not graduate you into a finished state. It graduates
        you into a way of working that updates itself. You have a way to work
        that survives the next model, the next tool, the next reorganization.
        Come back when you need to recalibrate.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to="/dashboard"
          className="rounded-md border border-emerald-500/40 px-4 py-2 text-sm font-medium text-emerald-300 hover:bg-emerald-500/[0.08]"
        >
          Back to the program
        </Link>
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Constare Academy · Class of today
        </span>
      </div>
    </div>
  );
}

/* --- Stage helper --- */
function StageBlock({
  label,
  body,
  accent,
}: {
  label: string;
  body: string;
  accent: 'emerald' | 'amber';
}) {
  const isEm = accent === 'emerald';
  const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`mt-5 rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
        {label}
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'The brief',
    'The map',
    '1 · Inventory',
    '2 · Draft 8 sections',
    '3 · Pressure-test',
    '4 · Ship to yourself',
    'Carry forward',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
